import type { ILink, IPagePath } from "@contentful-api/types/contentful";
import { usePathname } from "next/navigation";
import { useLocale } from "next-intl";
import { getPathname, pathnames } from "i18n/routing";
import { useEffect, useState } from "react";
import { linkTypeGuard } from "../utils/linkTypeGuard";

const HIDDEN_ROUTES = ["/mi-cuenta/direcciones", "/my-account/addresses"];

type Config = {
    links: (ILink | IPagePath)[];
};

export const useLink = ({ links }: Config) => {
    const pathname = usePathname();
    const locale = useLocale();
    const [activeLink, setActiveLink] = useState<number | null>(null);

    const getEnglishPathname = (localizedPath: string): string => {
        for (const [englishPath, localizedPaths] of Object.entries(pathnames)) {
            if (localizedPaths[locale] === localizedPath) {
                return englishPath;
            }
        }
        return localizedPath;
    };

    const getSlug = (link: ILink | IPagePath) =>
        linkTypeGuard(link)
            ? link?.fields?.internalLink?.fields?.completeSlug
            : link?.fields?.completeSlug;

    const onChangeActiveLink = (index: number) => () => setActiveLink(index);

    const isAddressesLink = (link: ILink | IPagePath) => HIDDEN_ROUTES.includes(getSlug(link));

    useEffect(() => {
        links.forEach((link, index) => {
            const decodedPath = decodeURI(pathname);
            const englishPath = getEnglishPathname(decodedPath);
            const localizedPathname = getPathname({ href: decodedPath, locale });

            const slug = getSlug(link);

            if (!slug) return;

            if ([englishPath, localizedPathname].some((path) => path.includes(slug))) {
                setActiveLink(index);
            }
        });
    }, [pathname, locale]);

    return { activeLink, isAddressesLink, onChangeActiveLink };
};
